/**
 * Cross-client context switching for deep links.
 *
 * The active client context lives in localStorage ("activeLicenseId") and the
 * whole app is scoped to it via API headers, so switching context requires a
 * reload. To make a switch feel like one continuous motion instead of a hard
 * reload, we stash the destination in sessionStorage before reloading; on boot
 * the ArrivalOverlay reads it back and finishes the animation in the new
 * context.
 *
 * Every context switch goes through here — the sidebar selector, the licenses
 * settings tab, and the cross-client deep-link guard.
 */

const SWITCH_KEY = "forge-context-switch"
const ACTIVE_LICENSE_KEY = "activeLicenseId"

export interface PendingContextSwitch {
  licenseId: number
  clientName: string
  domain: string
}

/**
 * Persist the destination, swap the active license, and reload into it.
 * Pass `destination` to land on a specific path instead of reloading in place
 * (the sidebar uses this to leave task views, which are not site-scoped).
 */
export function beginContextSwitch(target: PendingContextSwitch, destination?: string) {
  try {
    sessionStorage.setItem(SWITCH_KEY, JSON.stringify(target))
  } catch {
    // Private-mode storage failures just mean no arrival animation.
  }
  localStorage.setItem(ACTIVE_LICENSE_KEY, String(target.licenseId))
  // Cached responses belong to the outgoing client — drop them.
  localStorage.removeItem("forge-query-cache")
  if (destination) {
    window.location.href = destination
  } else {
    window.location.reload()
  }
}

/**
 * How long to hold the "leaving" overlay before the reload cuts it over: long
 * enough for the card to paint, short enough not to read as a stall.
 */
export function switchHoldMs(): number {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 50 : 450
}

/** Read-and-clear the pending switch. Returns null when this load isn't an arrival. */
export function consumePendingSwitch(): PendingContextSwitch | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(SWITCH_KEY)
    if (raw) sessionStorage.removeItem(SWITCH_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed?.licenseId === "number" && typeof parsed?.clientName === "string") {
      return parsed as PendingContextSwitch
    }
  } catch {
    // Corrupt payload — treat as no arrival.
  }
  return null
}
